<script lang="ts">
  // A chamfered "data window": a crisp neon edge drawn with the double-clip trick
  // (cyan outer chamfer + slightly inset surface chamfer = a glowing ring), a
  // faint hex-grid fill, and a brighter top hairline. The VRAINS panel primitive.
  import type { Snippet } from "svelte";

  let {
    children,
    accent = "cyan",
    c = 16,
    class: cls = "",
    glow = true,
    fill = false,
    grid = true,
  }: {
    children: Snippet;
    accent?: "cyan" | "magenta";
    c?: number;
    class?: string;
    glow?: boolean;
    /** Stretch to the parent's height (so an inner scroll region can fill it). */
    fill?: boolean;
    /** Hex-grid fill texture. Turn off for small, dense panels (e.g. login) where it reads as noise. */
    grid?: boolean;
  } = $props();

  // .tp-panel-edge / .tp-panel are stable theming hooks: a texture pack styles every data-window edge
  // and body by targeting them (the injected CSS is unlayered, so it beats these Tailwind defaults).
  const edge = $derived(accent === "magenta" ? "bg-magenta/80" : "bg-cyan/70");
  const glowCls = $derived(glow ? (accent === "magenta" ? "glow-magenta" : "glow-cyan") : "");
</script>

<div class="tp-panel-edge chamfer {edge} {glowCls} p-px {fill ? 'h-full' : ''}" style="--c:{c}px">
  <div class="tp-panel chamfer relative bg-surface/90 {grid ? 'grid-tex' : ''} {fill ? 'h-full' : ''} {cls}" style="--c:{c - 1}px">
    <!-- top hairline -->
    <div
      class="pointer-events-none absolute inset-x-6 top-0 h-px"
      style="background: linear-gradient(90deg, transparent, color-mix(in oklab, var(--color-cyan) 70%, transparent), transparent)"
    ></div>
    <!-- HUD corner tick (bottom-right): two diagonal hairlines, console flavour -->
    <span class="pointer-events-none absolute bottom-2 right-2 -rotate-45 opacity-45" aria-hidden="true">
      <span class="mb-[3px] block h-px w-4 {accent === 'magenta' ? 'bg-magenta' : 'bg-cyan'}"></span>
      <span class="ml-auto block h-px w-2.5 {accent === 'magenta' ? 'bg-magenta' : 'bg-cyan'}"></span>
    </span>
    {@render children()}
  </div>
</div>
